import { call } from '@advanza/api'
import InputField from '@trustoo/components/input/InputField'
import LoginBox from '@trustoo/components/login/LoginBox'
import LoginButton from '@trustoo/components/login/LoginButton'
import { useEnterKeyPress } from '@trustoo/hooks/useEnterKeyPress'
import { getLogoUrls } from '@trustoo/utils/localeUtils'
import LoginError from 'components/login/LoginError'
import { getRedirectAfterLogin } from 'components/login/redirectAfterLogin'
import { ChangeEvent, useState } from 'react'
import isEmail from 'validator/es/lib/isEmail'

interface LoginErrors {
    email?: string
    password?: string
    login?: string
}

const Login = () => {
    const [email, setEmail] = useState(
        () => new URLSearchParams(window.location.search).get('email') ?? '',
    )
    const [password, setPassword] = useState('')
    const [rememberMe, setRememberMe] = useState(true)
    const [errors, setErrors] = useState<LoginErrors>({})
    const [isLoading, setIsLoading] = useState(false)

    const login = () => {
        const fieldErrors: LoginErrors = {
            ...(!isEmail(email) && { email: 'Enter a valid email address' }),
            ...(password === '' && { password: 'Enter your password' }),
        }
        setErrors(fieldErrors)
        if (Object.keys(fieldErrors).length > 0) {
            return
        }

        setIsLoading(true)
        call('auth/login', {
            payload: { email, password, remember_me: rememberMe ? '1' : '0' },
        }).then(
            (response: Parameters<typeof getRedirectAfterLogin>[0]) => {
                window.location.href = getRedirectAfterLogin(response)
            },
            () => {
                setIsLoading(false)
                setErrors({ login: 'The email address or password is incorrect' })
            },
        )
    }

    useEnterKeyPress(login, !isLoading && !window._user)

    const onChange =
        (setValue: (value: string) => void, field: keyof LoginErrors) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value)
            setErrors((previous) => ({ ...previous, [field]: undefined, login: undefined }))
        }

    return (
        <LoginBox title="Trustoo office" logoUrl={getLogoUrls().default} isLoading={isLoading}>
            {window._user ? (
                <p className="pt-4">
                    You are already logged in as {window._user.email}. If you want to log in as a
                    different user, first{' '}
                    <a className="text-[#5788ff]" href="/api/auth/logout">
                        log out
                    </a>
                    .
                </p>
            ) : (
                <div className="flex flex-col gap-4 pt-4">
                    <InputField
                        name="email"
                        type="email"
                        value={email}
                        onChange={onChange(setEmail, 'email')}
                        error={errors.email}
                        placeholder="Email address"
                        autoFocus
                    />
                    <InputField
                        name="password"
                        type="password"
                        value={password}
                        onChange={onChange(setPassword, 'password')}
                        error={errors.password}
                        placeholder="Password"
                    />
                    {errors.login && <LoginError message={errors.login} />}
                    <div className="flex items-center">
                        <label className="flex cursor-pointer items-center gap-2">
                            <input
                                type="checkbox"
                                className="size-4 appearance-auto"
                                name="remember_me"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            Remember me
                        </label>
                    </div>
                    <LoginButton onClick={login} disabled={isLoading} className="w-full">
                        Log in
                    </LoginButton>
                    <p className="m-0 text-center text-md text-[#777da5]">
                        Forgot your password? Ask an admin to send you a password mail.
                    </p>
                </div>
            )}
        </LoginBox>
    )
}

export default Login
